import {
  Button,
  Dialog,
  DialogClose,
  Radio,
  Tooltip,
} from "@hellocarbo/lasagne";
import { Download, LoaderCircle } from "lucide-react";
import { use, useState } from "react";
import { useTranslation } from "react-i18next";

import CertificateSvg from "@/assets/common/other_icons/certificate.svg?react";
import { PortalContainerContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";

import BaseCard from "./BaseCard";

function DownloadAttestation({
  reviewId,
  companyHasSubsidiaries,
  hasOtherReviewsAtSameDate,
  isMultientityDashboard = false,
  disabled = false,
  disabledTooltip,
}: {
  reviewId?: number;
  companyHasSubsidiaries?: boolean;
  hasOtherReviewsAtSameDate?: boolean;
  isMultientityDashboard?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}) {
  const { t } = useTranslation(["communicate", "common", "dashboard"]);
  const { sendSegmentEvent } = useSegmentEvent();
  const { container } = use(PortalContainerContext);

  const [template, setTemplate] = useState<
    "fr" | "en" | "fr_consolidated" | "en_consolidated"
  >(isMultientityDashboard ? "fr_consolidated" : "fr");
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const multientityTriggerLabel = isMultientityDashboard
    ? t("dashboard:analysisAndCommunication.downloadAttestation")
    : undefined;

  const handleDownload = async () => {
    setShowLoadingOverlay(true);

    const isConsolidated = template.includes("consolidated");
    const languageParam = template.replace("_consolidated", "");
    const endpoint = isConsolidated
      ? `/api/client/reviews/${reviewId}/generate_consolidated_certificate?language=${languageParam}`
      : `/api/client/reviews/${reviewId}/generate_certificate?language=${languageParam}`;

    const response = await fetch(endpoint, {
      method: "POST",
    });

    const data = await response.json();

    const url = languageParam === "fr" ? data.fr_url : data.en_url;

    if (url) {
      sendSegmentEvent("certificate_downloaded");
      window.open(url, "_blank");
      setShowLoadingOverlay(false);
      return;
    }

    if (data.enqueued) {
      const checkCertificate = async () => {
        const checkResponse = await fetch(endpoint, { method: "POST" });
        const checkData = await checkResponse.json();
        const checkUrl =
          languageParam === "fr" ? checkData.fr_url : checkData.en_url;

        if (checkUrl) {
          window.open(checkUrl, "_blank");
          setShowLoadingOverlay(false);
        } else {
          setTimeout(() => void checkCertificate(), 2000);
        }
      };
      sendSegmentEvent("certificate_downloaded");
      checkCertificate();
    }
  };

  const disabledButton = (
    <div className="mt-xs">
      <Button shape="soft" intent="secondary" disabled>
        <Download />
        {multientityTriggerLabel ??
          t("communicate:downloadAttestation.modal.trigger")}
      </Button>
    </div>
  );

  const content = disabled ? (
    disabledTooltip ? (
      <Tooltip
        portalContainer={container as Element}
        side="top"
        content={disabledTooltip}
        triggerAsChild
      >
        {disabledButton}
      </Tooltip>
    ) : (
      disabledButton
    )
  ) : (
    <>
      <Dialog
        title={t("communicate:downloadAttestation.modal.title")}
        description={t("communicate:downloadAttestation.modal.description")}
        portalContainer={container as Element}
        trigger={
          <div className="mt-xs">
            <Button shape="soft" intent="secondary">
              <Download />
              {multientityTriggerLabel ??
                t("communicate:downloadAttestation.modal.trigger")}
            </Button>
          </div>
        }
        footer={
          <>
            <DialogClose asChild>
              <Button shape="link">
                {t("communicate:downloadAttestation.modal.cancel")}
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button shape="solid" onClick={handleDownload}>
                {t("communicate:downloadAttestation.modal.validate")}
              </Button>
            </DialogClose>
          </>
        }
      >
        <Radio
          type="button"
          name="attestation_language"
          label={t("communicate:downloadAttestation.modal.radio_label")}
          defaultValue={template}
          items={[
            ...(isMultientityDashboard
              ? []
              : [
                  {
                    label: t(
                      "communicate:downloadAttestation.modal.fr_simple_label"
                    ),
                    value: "fr",
                  },
                  {
                    label: t(
                      "communicate:downloadAttestation.modal.en_simple_label"
                    ),
                    value: "en",
                  },
                ]),
            ...(isMultientityDashboard ||
            (companyHasSubsidiaries && hasOtherReviewsAtSameDate)
              ? [
                  {
                    label: t(
                      "communicate:downloadAttestation.modal.fr_consolidated_label"
                    ),
                    value: "fr_consolidated",
                  },
                  {
                    label: t(
                      "communicate:downloadAttestation.modal.en_consolidated_label"
                    ),
                    value: "en_consolidated",
                  },
                ]
              : []),
          ]}
          onValueChange={(value: unknown) => {
            if (
              typeof value === "string" &&
              ["fr", "en", "fr_consolidated", "en_consolidated"].includes(value)
            ) {
              setTemplate(
                value as "fr" | "en" | "fr_consolidated" | "en_consolidated"
              );
            }
          }}
        />
      </Dialog>
      {showLoadingOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="gap-md p-lg flex flex-col items-center rounded-lg bg-white shadow-lg">
            <LoaderCircle className="text-primary h-10 w-10 animate-spin" />
            <p className="text-center">{t("common:loading")}</p>
          </div>
        </div>
      )}
    </>
  );

  if (isMultientityDashboard) {
    return content;
  }

  return (
    <BaseCard
      title={t("communicate:downloadAttestation.title")}
      text={t("communicate:downloadAttestation.text")}
      Icon={CertificateSvg}
    >
      {content}
    </BaseCard>
  );
}

export default DownloadAttestation;
